import {
  CourseMaterial,
  useCoursesLoaderData,
  useProgramDetails,
} from "../coursesLoader.ts";
import { useBrandLoaderData } from "routes/root/rootLoader.ts";
import { DeweySchema } from "routes/root/helpers/brandSchema.ts";
import useVirtualStore from "routes/home/common/virtual-store/useVirtualStore.tsx";
import { useCallback, useMemo } from "react";
import { usePreferredShippingAddress } from "../usePreferredShippingAddress.tsx";

const useTermOrdersBanner = () => {
  const { allMaterials, courses } = useCoursesLoaderData();
  const { scope } = useProgramDetails();
  const brand: DeweySchema = useBrandLoaderData();
  const { isVirtualStore } = useVirtualStore();
  const { preferredShippingAddress } = usePreferredShippingAddress();

  const coursesAsLearner = useMemo(() => {
    return courses
      .filter((course) => course.userRole === "learner")
      .map((course) => course.id);
  }, [courses]);

  const printMaterials = useMemo(() => {
    return allMaterials.filter(
      (material) =>
        material.materialFormat === "print" &&
        coursesAsLearner.includes(material.courseId)
    );
  }, [allMaterials, coursesAsLearner]);

  const getBannersInfo = useCallback(() => {
    const result = {
      verifyAddress: true,
      shippingReturns: [],
      campusReturns: [],
    };
    const coursesAsLearner = courses.filter(
      (course) => course.userRole === "learner"
    );
    if (coursesAsLearner.length === 0) {
      return {
        ...result,
        isLearner: false,
        verifyAddress: false,
      };
    }
    return printMaterials.reduce(
      (
        acc: {
          shippingReturns: CourseMaterial[];
          campusReturns: CourseMaterial[];
          verifyAddress: boolean;
        },
        material
      ) => {
        if (material.orderInfo && acc.verifyAddress) {
          acc.verifyAddress = false;
        }
        if (material.orderInfo?.returnStatus !== "Needs to be Returned") {
          return acc;
        }
        if (material.orderInfo.returnMethod === "Campus") {
          acc.campusReturns.push(material);
        } else {
          acc.shippingReturns.push(material);
        }
        return acc;
      },
      result
    );
  }, [courses, printMaterials]);

  const shouldDisplayBanners = useMemo(
    () => isVirtualStore && scope === "ea_only" && printMaterials.length > 0,
    [scope, isVirtualStore, printMaterials.length]
  );

  if (!shouldDisplayBanners) {
    return {
      shippingReturns: [],
      campusReturns: [],
      verifyAddress: false,
      preferredShippingAddress,
    };
  }

  const { shippingReturns, campusReturns, verifyAddress } = getBannersInfo();

  return {
    shippingReturns,
    campusReturns,
    verifyAddress:
      brand.VirtualStore.EA.enableShippingAddressChanges && verifyAddress,
    preferredShippingAddress,
  };
};

export default useTermOrdersBanner;
